import {observer} from "mobx-react-lite";
import type {ReactElement} from "react";
import {useEffect, useState} from "react";
import {
    Checkbox,
    Chip,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import {CurrencyComponent} from "../../../components/currency-component/CurrencyComponent";
import {ChevronRight, CompareArrowsOutlined, KeyboardArrowDown} from "@mui/icons-material";
import {getCollapseAnimationTiming} from "../../../components/table/utils";
import {useStores} from "../../../stores";

interface Props {
    h: {key: string; label: string; type: string}[];
    className?: string;
    data?: any[];
    isLoading?: boolean;
    compactNumbers?: boolean;
    pageSize?: number;
    pageSizeOptions?: number[];
    container?: React.ElementType;
    notEditable?: boolean;
    onSelect?: (item: any) => void;
    subTable?: React.FC<SubTableProps>;
}

interface SubTableProps {
    h: {key: string; label: string; type: string}[];
    className?: string;
    data?: any[];
    isLoading?: boolean;
    pageSize?: number;
    pageSizeOptions?: number[];
}

export const ContractAnalyticsTable: React.FC<Props> = observer(
    ({
         h,
         className,
         data,
         isLoading,
         compactNumbers = false,
         pageSize,
         pageSizeOptions,
         container,
         notEditable,
         onSelect,
         subTable: SubTableComponent,
     }) => {
        const {contractAnalyticsStore} = useStores();
        const containerComponent = container || 'div';
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(pageSize || 8);
        const [rowsPerPageOptions] = useState(pageSizeOptions || [8, 15, 25, 50]);
        // const [colorScalesMap, setColorScalesMap] = useState<{ [key: string]: { positive: any; negative: any } }>({});
        const [openRows, setOpenRows] = useState<number[]>([]);

        const headers = SubTableComponent ? [{key: 'dropdown', label: '', type: 'dropdown'}, ...h] : h;

        useEffect(() => {
            if (page === 0) return;
            if (isLoading) setPage(0);
            const maxPage = Math.floor((data?.length || 0) / rowsPerPage);
            if (page > maxPage) {
                setPage(0);
            }
        }, [data?.length, rowsPerPage, page, isLoading]);

        const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        const toggleRow = (index: number) => {
            setOpenRows(prevOpenRows => {
                if (prevOpenRows.includes(index)) {
                    // If the index is already open, remove it
                    return prevOpenRows.filter(row => row !== index);
                } else {
                    // If the index is closed, add it
                    return [...prevOpenRows, index];
                }
            });
        };

        const renderSubTable = (index: number) => {
            if (!SubTableComponent || !openRows.includes(index)) return null; // Check if index is open
            return <SubTableComponent h={headers} className={className} data={data || []} isLoading={isLoading}
                                      pageSize={pageSize} pageSizeOptions={pageSizeOptions}/>;
        };

        const tableClass = `contract-analytics-table-wrapper${className ? ` ${className}` : ''}${isLoading ? ' loading' : ''}`;

        return (
            <TableContainer component={containerComponent} className={tableClass}>
                <Table cellPadding={0}>
                    <TableHead>
                        <TableRow>
                            {headers.map((header) => (
                                <TableCell key={header.key}>{header.label}</TableCell>
                            ))}
                        </TableRow>
                        {/*<tr className={`progress-row${isLoading ? ' loading' : ''}`}>*/}
                        {/*    <td colSpan={headers.length}>{isLoading && <LinearProgress variant="indeterminate"/>}</td>*/}
                        {/*</tr>*/}
                    </TableHead>
                    <TableBody>
                        {(rowsPerPage > 0 ? data?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) || [] : data || []).map((item, index) => (
                            <>
                                <TableRow
                                    key={JSON.stringify(item)}
                                    className="contract-analytics-table-row"
                                    onClick={() => toggleRow(index)}
                                >
                                    {headers.map((header) => {
                                        const value = item[header.key];
                                        let cellNumberClass = '';
                                        const style: React.CSSProperties = {};
                                        let cellContent: ReactElement;
                                        switch (header.type) {
                                            case 'currency':
                                                cellContent = compactNumbers ? (
                                                    <CurrencyComponent v={value} nDigits={3}/>
                                                ) : (
                                                    <>{value}</>
                                                );
                                                cellNumberClass = 'fit-number';
                                                break;
                                            case '2_decimal':
                                                cellContent = <>{(Math.round(value * 100) / 100).toFixed(2)}</>;
                                                break;
                                            case 'category':
                                                cellContent = <>{value}</>;
                                                break;
                                            case 'dropdown':
                                                cellContent = openRows.includes(index) ?
                                                    <IconButton>
                                                        <KeyboardArrowDown/>
                                                    </IconButton>
                                                    :
                                                    <IconButton>
                                                        <ChevronRight/>
                                                    </IconButton>;
                                                break;
                                            case 'selection':
                                                if (notEditable) {
                                                    cellContent = <></>;
                                                } else {
                                                    cellContent = (
                                                        <Checkbox
                                                            checked={item.selected}
                                                            onChange={(e) => {
                                                                if (!onSelect) return;
                                                                onSelect(item);
                                                            }}
                                                        />
                                                    );
                                                }
                                                break;
                                            case 'chip':
                                                if (value == 'In contract') {
                                                    cellContent = <Chip label={value}
                                                                        variant="outlined" color="success"
                                                                        style={{
                                                                            width: '100%',
                                                                            border: '1px solid #08A57A',
                                                                            color: '#08A57A'
                                                                        }}/>;
                                                } else if (value == 'Out of contract') {
                                                    cellContent =
                                                        <Chip label={value}
                                                              variant="outlined" color={"error"}
                                                              style={{
                                                                  width: '100%',
                                                                  border: '1px solid #FF3435 ',
                                                                  color: '#FF3435'
                                                              }}
                                                        />;
                                                } else {
                                                    cellContent = <Chip label={value}/>;
                                                }

                                                break;
                                            case 'move':
                                                cellContent = <IconButton
                                                    onClick={() => {
                                                        // contractAnalyticsStore.setMoveTransactionData()
                                                        contractAnalyticsStore.toggleMoveModal()
                                                    }}
                                                    style={{color: '#091344'}}>
                                                    <CompareArrowsOutlined/>
                                                </IconButton>;
                                                break;
                                            default:
                                                cellContent = <>{value}</>;
                                                break;
                                        }
                                        // if (colorScalesMap[header.key]) {
                                        //     const {positive, negative} = colorScalesMap[header.key];
                                        //     const bgColor = value >= 0 ? positive(value) : negative(value);
                                        //     const fgColor = d3.hsl(bgColor).l > 0.5 ? 'black' : 'white';
                                        //     style.backgroundColor = bgColor;
                                        //     style.color = fgColor;
                                        // }
                                        return (
                                            <TableCell key={header.key}
                                                       className={`contract-analytics-table-cell ${cellNumberClass}`}
                                                       style={style}>
                                                <Typography>{cellContent}</Typography>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                                {SubTableComponent && <TableRow>
                                    <TableCell colSpan={headers.length} className={'sub-table-row'}>
                                        <Collapse in={openRows.includes(index)}
                                                  timeout={getCollapseAnimationTiming([1, 2, 3, 4, 4])} // Check if index is open
                                                  unmountOnExit>
                                            {renderSubTable(index)}
                                        </Collapse>
                                    </TableCell>
                                </TableRow>}
                            </>
                        ))}
                        {(!data || data.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={headers.length} align="center" style={{height: '100px'}}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '25em'
                                    }}>
                                        No data available
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    {/*<TableFooter>*/}
                    {/*    <TableRow>*/}
                    {/*        <TablePagination*/}
                    {/*            rowsPerPageOptions={rowsPerPageOptions}*/}
                    {/*            colSpan={headers.length}*/}
                    {/*            count={data?.length || 0}*/}
                    {/*            rowsPerPage={rowsPerPage}*/}
                    {/*            page={page}*/}
                    {/*            showFirstButton*/}
                    {/*            showLastButton*/}
                    {/*            SelectProps={{*/}
                    {/*                inputProps: {'aria-label': 'rows per page'},*/}
                    {/*                native: true,*/}
                    {/*            }}*/}
                    {/*            onPageChange={handleChangePage}*/}
                    {/*            onRowsPerPageChange={handleChangeRowsPerPage}*/}
                    {/*            ActionsComponent={TablePaginationActions}*/}
                    {/*        />*/}
                    {/*    </TableRow>*/}
                    {/*</TableFooter>*/}
                </Table>
            </TableContainer>
        );
    }
);
